import { useState } from 'react'
import { Download } from 'lucide-react'

import { columns } from './constants'
import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { GetMemberEventLogTableData } from '@/modules/manage-member/type'
import { DataTableFilterSelector } from '@/common/ui/table/data-table-selector-with-query'
import { OperatorEventHistoryStatus } from '@/schema'
import { DataTableResetFilter } from '@/common/ui/table/data-table-reset-filter'
import { tagEventMapper } from '@/modules/manage-member/mappers'
import { DataTableSearch } from '@/common/ui/table/data-table-search'
import { Button } from '@/common/ui/button'
import { ExportCsvAmountModal } from '@/common/components/ExportCsvAmountModal'
import useMutationExportEventLogCsv from '@/modules/manage-member/api/hooks/useMutationExportEventLogCsv'
import { GetMemberEventLogRequest } from '@/modules/manage-member/type'

// Same columns the table shows, so the file matches what was on screen.
const CSV_COLUMN_MAPPINGS: Record<string, string> = {
	operatorName: 'ผู้ดูแลระบบ',
	eventName: 'ชื่อการทำรายการ',
	description: 'รายละเอียด',
	status: 'สถานะ',
	tagEvent: 'TAG',
	ipAddress: 'IP Address',
	createdAt: 'วันที่ทำรายการ',
}

interface EventLogTableProps {
	data: GetMemberEventLogTableData[]
	total: number
	isLoading?: boolean
	/** Current filters, so the export covers the same rows as the table. */
	filters: GetMemberEventLogRequest
}

export default function EventLogTable({
	data,
	total,
	isLoading,
	filters,
}: EventLogTableProps) {
	const [showExportModal, setShowExportModal] = useState(false)
	const exportCsvMutation = useMutationExportEventLogCsv()

	const statusOptions = [
		{
			label: 'สำคัญ',
			value: OperatorEventHistoryStatus.IMPORTANT,
		},
		{
			label: 'ปกติ',
			value: OperatorEventHistoryStatus.NORMAL,
		},
	]

	const tagEventOptions = Object.entries(tagEventMapper).map(
		([value, label]) => ({
			label,
			value,
		}),
	)

	const handleExportCsv = (amount: number) => {
		exportCsvMutation.mutate({
			...filters,
			skip: 0,
			take: amount || total || 1000,
			filename: 'operator_event_log',
			columnMappings: CSV_COLUMN_MAPPINGS,
		})
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-4">
				<div className="mt-5 flex gap-2">
					<DataTableSearch placeHolder="ค้นหาผู้ดูแลระบบ" />
					<DataTableResetFilter />
				</div>

				<DataTableFilterSelector
					options={statusOptions}
					placeholder="เลือกสถานะ"
					filterKey="status"
					label="สถานะ"
					className="w-48"
				/>
				<DataTableFilterSelector
					options={tagEventOptions}
					placeholder="เลือก TAG"
					filterKey="tagEvent"
					label="TAG"
					className="w-64"
				/>

				<Button
					variant="outline"
					size="sm"
					onClick={() => setShowExportModal(true)}
					disabled={exportCsvMutation.isPending || !data.length}
					className="mt-5 flex items-center gap-2"
				>
					<Download className="h-4 w-4" />
					โหลด CSV
				</Button>
			</div>
			<DataTablePaginate
				columns={columns}
				data={data}
				totalItems={total}
				isLoading={isLoading}
			/>
			<ExportCsvAmountModal
				open={showExportModal}
				onOpenChange={setShowExportModal}
				onConfirm={handleExportCsv}
				defaultAmount={total || 1000}
				maxAmount={100000}
				isLoading={exportCsvMutation.isPending}
			/>
		</div>
	)
}
